import React, { useEffect, useState } from 'react';

import CropOutlinedIcon from '@mui/icons-material/CropOutlined';
import PolylineOutlinedIcon from '@mui/icons-material/PolylineOutlined';
import {
    Box,
    Button,
    Grid,
    Typography,
    UiDialog as Dialog,
} from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import {
    ReactHookFormIntlNumberInput,
    ReactHookFormToggleButtonGroup,
} from 'common/components/form/bootstrap';
import {
    POLYGON_SEGMENT,
    POLYGON_RECTANGLE,
} from 'common/components/segment/constants';
import RenderInputsPerSystem from 'common/components/segment/RenderInputsPerSystem';
import RoofAndSystemTypeSelects from 'common/components/segment/RoofAndSystemTypeSelects';
import { arraytoDictionary } from 'common/utils/helpers';
import {
    getSlopeOptions,
    getMinMaxBuildHeight,
    getMinMaxCeilTilt,
    getMinMaxFrontBaseHeight,
    getMinMaxOuterRailLength,
    getMinMaxSeamDistance,
} from 'common/utils/helpers/systemsValues';
import yupResolver from 'common/utils/yupResolver';

import createProposalModal from '../../createProposalModal';
import * as profileSelectors from '../../profile/selectors';
import * as systemsSelectors from '../../systems/selectors';
import * as actions from '../actions';
import * as selectors from '../selectors';

import validate from './forms/validateAddSegmentForm';

const AddSegmentFormContainer = ({
    fetchPreset,
    fetchSystemTypes,
    editionPayload,
    isOpen,
    parentFieldSegmentData,
    presetsData,
    presetScope,
    roofTypes,
    setAddSegmentModalEditionMode,
    setAddSegmentModalOpenStatus,
    setDrawingMode,
    setNewSegmentConfig,
    systemTypes,
    updateSegmentSettings,
}) => {
    const { t } = useTranslation();
    const [presets, setPresets] = useState([]);
    const [minBuildHeight] = getMinMaxBuildHeight();
    const [minOuterRailLength] = getMinMaxOuterRailLength();
    const [minFrontBaseHeight] = getMinMaxFrontBaseHeight();
    const editionForm = editionPayload;

    const isEditionMode = editionForm.isEditionMode || false;
    const segmentId = editionForm.segmentId;
    let editionData = editionForm.editionData || {};

    const defaultVals = React.useMemo(
        () => ({
            building_height: minBuildHeight,
            category: '',
            ceil_tilt: 0,
            drawing_mode: `${POLYGON_SEGMENT}`,
            fill_algorithm: 'fill',
            quantity: 0,
            seam_distance: '',
            sheet_gauge: '',
            sloof_type: getSlopeOptions()[0]?.value || '',
            system: '',
            system_uid: '',
            type_application: '',
            rail_spacing: { strategy: 'automatic', value: [] },
            rail_support_length: { strategy: 'automatic', value: '' },
            plate_support_length: { strategy: 'automatic', value: '' },
            beam_spacing: { strategy: 'automatic', value: '' },
            support_length: { strategy: 'automatic', value: '' },
            lfoot_supports_width: { strategy: 'automatic', value: '' },
            gclamp_type: '',
            outer_rail_length: {
                strategy: 'automatic',
                value: minOuterRailLength,
            },
            front_base_height: {
                strategy: 'automatic',
                value: minFrontBaseHeight,
            },
        }),
        [minBuildHeight, minFrontBaseHeight, minOuterRailLength],
    );

    const initialValues = React.useMemo(() => {
        let vals = { ...defaultVals };

        if (isEditionMode) {
            vals = { ...vals, ...editionData };

            if (editionData.buildingHeight !== undefined) {
                vals.building_height = editionData.buildingHeight;
            }
            if (editionData.roofSlopeDeg !== undefined) {
                vals.ceil_tilt = editionData.roofSlopeDeg;
            }

            if (
                editionData.panelFillMode === 'units' &&
                editionData.targetPanelCount !== undefined
            ) {
                vals.fill_algorithm = 'u';
                vals.quantity = editionData.targetPanelCount;
            } else if (
                editionData.panelFillMode === 'kw' &&
                editionData.targetKw !== undefined
            ) {
                vals.fill_algorithm = 'kW';
                vals.quantity = editionData.targetKw;
            } else if (
                editionData.panelFillMode === 'fill' ||
                editionData.fill_algorithm === 'fill'
            ) {
                vals.fill_algorithm = 'fill';
                vals.quantity = 0;
            }
        }
        return vals;
    }, [isEditionMode, editionData, defaultVals]);

    const { control, handleSubmit, reset, setValue } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    useEffect(() => {
        if (isOpen) {
            reset(initialValues);
            if (isEditionMode && !isEmpty(initialValues.type_application)) {
                fetchSystemTypes(initialValues.type_application);
            }
        }
    }, [isOpen, isEditionMode, fetchSystemTypes, initialValues, reset]);

    useEffect(() => {
        if (isOpen && isEditionMode && systemTypes && systemTypes.length > 0) {
            const currentSystem = systemTypes.find(
                (sys) => sys.uniqueid === initialValues.system_uid,
            );
            if (currentSystem) {
                setValue('system', currentSystem.id);
            }
        }
    }, [
        isOpen,
        isEditionMode,
        systemTypes,
        initialValues.system_uid,
        setValue,
    ]);

    useEffect(() => {
        if (presetScope === 'COMPANY')
            setPresets(presetsData.filter((p) => p.is_preset_company));
        else setPresets(presetsData.filter((p) => !p.is_preset_company));
    }, [presetScope]);

    const [fill_algorithm, presetId, system_uid, type_application] = useWatch({
        control,
        name: [
            'fill_algorithm',
            'preset_configuration',
            'system_uid',
            'type_application',
        ],
    });

    useEffect(() => {
        if (presetId) fetchPreset(presetId);
    }, [presetId]);

    const systemsDictionary = arraytoDictionary(systemTypes, 'id');
    const presetsDictionary = arraytoDictionary(presets, 'system_uniqueid');

    const { getRoofTypesOptions, getSystemTypesOptions } =
        createProposalModal.helpers;

    const onClose = () => {
        setAddSegmentModalOpenStatus(false);
        if (isEditionMode && setAddSegmentModalEditionMode) {
            setAddSegmentModalEditionMode({
                isEditionMode: false,
                editionData: {},
            });
        }
        reset();
    };

    const onChangeTypeApplication = (e) => {
        const type = e.target.value;

        setValue('ceil_tilt', 0);
        setValue('sloof_type', '');
        setValue('system', '');
        setValue('system_uid', '');
        setValue('rail_spacing', { strategy: 'automatic', value: [] });
        setValue('rail_support_length', { strategy: 'automatic', value: '' });
        setValue('plate_support_length', { strategy: 'automatic', value: '' });
        setValue('beam_spacing', { strategy: 'automatic', value: '' });
        setValue('support_length', { strategy: 'automatic', value: '' });
        setValue('lfoot_supports_width', { strategy: 'automatic', value: '' });
        setValue('gclamp_type', '');
        setValue('outer_rail_length', {
            strategy: 'automatic',
            value: minOuterRailLength,
        });
        setValue('front_base_height', {
            strategy: 'automatic',
            value: minFrontBaseHeight,
        });

        if (!isEmpty(type)) fetchSystemTypes(type);
    };

    const onChangeSystem = (e) => {
        const system = e.target.value;
        const newSystemUid = systemsDictionary[system]?.uniqueid || '';
        const preset = presetsDictionary[newSystemUid];
        const presetConfiguration = preset?.preset_configuration || null;
        setValue('system_uid', newSystemUid);

        try {
            const slopeOptsForSystem = getSlopeOptions(newSystemUid);
            if (slopeOptsForSystem && slopeOptsForSystem.length > 0) {
                setValue('sloof_type', slopeOptsForSystem[0].value || '');
            }
        } catch (err) {
            console.warn(`Could not get slope options`, err);
        }

        const [minCeilTilt] = getMinMaxCeilTilt(newSystemUid);
        const [minSeamDistance] = getMinMaxSeamDistance(newSystemUid);
        setValue('ceil_tilt', minCeilTilt);
        setValue('seam_distance', minSeamDistance);
        setValue('preset_configuration', null);

        if (presetConfiguration && !isEmpty(presetConfiguration)) {
            setValue('preset_configuration', preset?.id);
            Object.entries(presetConfiguration).forEach(
                ([fieldName, fieldConfig]) => {
                    setValue(fieldName, fieldConfig);
                },
            );
        }
    };

    const onChangeSloofType = (e) => {
        const sloofType = e.target.value;
        if (sloofType === 'double_slope') {
            setValue('drawing_mode', `${POLYGON_RECTANGLE}`);
        }
    };
    // Look up the human-readable name of the selected system from the
    // systemsDictionary loaded for the current type_application. The form
    // only persists `system` (id) and `system_uid`, so without this lookup
    // downstream consumers (3D library SegmentSummary, AlurackRoofEditExtra)
    // never receive the friendly name and can only show the opaque uid.
    const _resolveSystemName = (values) => {
        const id = values.system;
        const fromDict = id ? systemsDictionary[id]?.name : null;
        // Fall back to anything already on the values (edition mode keeps
        // it through editionData) so we don't accidentally clear it.
        return fromDict || values.system_name || '';
    };

    const createSegments = (values) => {
        const { quantity, fill_algorithm, drawing_mode } = values;
        const drawingMode = Number(drawing_mode);
        let newQuantity = quantity;
        if (fill_algorithm === 'fill') newQuantity = Infinity;
        if (fill_algorithm === 'kW') {
            const {
                proposal: {
                    panel: { max_power },
                },
            } = parentFieldSegmentData;
            newQuantity = Math.ceil((newQuantity / max_power) * 1000);
        }
        const newValues = {
            ...values,
            quantity: newQuantity,
            system_name: _resolveSystemName(values),
        };
        setNewSegmentConfig(newValues);
        setAddSegmentModalOpenStatus(false);
        setDrawingMode(true, drawingMode);
        reset();
    };
    const updateSegmentData = (values) => {
        const enrichedValues = {
            ...values,
            system_name: _resolveSystemName(values),
        };
        if (segmentId) {
            // Legacy (non-3D) flow: save directly to the API.
            updateSegmentSettings(segmentId, enrichedValues);
        } else {
            // 3D planner flow: no segmentId means the library owns the save.
            // Signal via setNewSegmentConfig so PreDrawFormBridge relays the
            // submitted values back to the library (which will sync its Zustand
            // store and persist them until the user clicks Save in the sidebar).
            setNewSegmentConfig(enrichedValues);
        }
        setAddSegmentModalOpenStatus(false);
        if (setAddSegmentModalEditionMode) {
            setAddSegmentModalEditionMode({
                isEditionMode: false,
                editionData: {},
            });
        }
        reset();
    };

    const onSubmit = (values) => {
        isEditionMode ? updateSegmentData(values) : createSegments(values);
    };

    return (
        <Dialog
            onClose={onClose}
            open={isOpen}
            size="sm"
            title={isEditionMode ? t('Edit structure') : t('Add segment')}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <RoofAndSystemTypeSelects
                    control={control}
                    disabled={isEditionMode}
                    onChangeSystem={onChangeSystem}
                    onChangeTypeApplication={onChangeTypeApplication}
                    roofTypesOptions={getRoofTypesOptions(roofTypes)}
                    systemTypesOptions={getSystemTypesOptions(systemTypes)}
                    systemUid={system_uid}
                    typeApplication={type_application}
                />

                <RenderInputsPerSystem
                    control={control}
                    isNew={!isEditionMode}
                    onChangeSloofType={onChangeSloofType}
                    systemUid={system_uid}
                />

                {!isEditionMode && (
                    <>
                        <Grid container sx={{ mb: 2 }}>
                            <Grid size="grow">
                                <ReactHookFormToggleButtonGroup
                                    control={control}
                                    fullWidth
                                    name="fill_algorithm"
                                    onChange={(_, value) => {
                                        if (value === null) return;
                                        setValue('quantity', 0);
                                    }}
                                    options={[
                                        { label: 'Fill', value: 'fill' },
                                        { label: 'u', value: 'u' },
                                        { label: 'kW', value: 'kW' },
                                    ]}
                                />
                            </Grid>

                            {fill_algorithm !== 'fill' && (
                                <Grid size={6}>
                                    <ReactHookFormIntlNumberInput
                                        append={
                                            fill_algorithm === 'kW' ? 'kW' : 'u'
                                        }
                                        control={control}
                                        fullWidth
                                        min={0}
                                        name="quantity"
                                        sx={{ mb: '0!important' }}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        <Box sx={{ mb: 2 }}>
                            <Typography>{t('Drawing tool')}</Typography>

                            <ReactHookFormToggleButtonGroup
                                control={control}
                                name="drawing_mode"
                                fullWidth
                                options={[
                                    {
                                        label: (
                                            <PolylineOutlinedIcon fontSize="small" />
                                        ),
                                        value: `${POLYGON_SEGMENT}`,
                                    },
                                    {
                                        label: (
                                            <CropOutlinedIcon fontSize="small" />
                                        ),
                                        value: `${POLYGON_RECTANGLE}`,
                                    },
                                ]}
                            />
                        </Box>
                    </>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                    }}
                >
                    <Button
                        onClick={onClose}
                        sx={{ mb: '0!important' }}
                        variant="text"
                    >
                        {t('Close')}
                    </Button>

                    <Button type="submit" sx={{ mb: '0!important' }}>
                        {t('Accept')}
                    </Button>
                </Box>
            </form>
        </Dialog>
    );
};

const mapStateToProps = createStructuredSelector({
    editionPayload: selectors.getAddSegmentModalEditionMode,
    isOpen: selectors.getAddSegmentModalOpenStatus,
    parentFieldSegmentData: selectors.getParentFieldSegmentData,
    presetsData: systemsSelectors.getFetchPresetsData,
    presetScope: profileSelectors.getPresetScope,
    roofTypes: createProposalModal.selectors.getRoofTypesData,
    systemTypes: createProposalModal.selectors.getSystemTypesData,
});

const mapDispatchToProps = (dispatch) => ({
    fetchPreset: (id) => dispatch(actions.fetchPreset(id)),
    fetchSystemTypes: (type) =>
        dispatch(createProposalModal.actions.fetchSystemTypes(type)),
    setAddSegmentModalEditionMode: (mode) =>
        dispatch(actions.setAddSegmentModalEditionMode(mode)),
    setAddSegmentModalOpenStatus: (isOpen) =>
        dispatch(actions.setAddSegmentModalOpenStatus(isOpen)),
    setDrawingMode: (isDrawingMode, type) =>
        dispatch(actions.setDrawingMode(isDrawingMode, type)),
    setNewSegmentConfig: (config) =>
        dispatch(actions.setNewSegmentConfig(config)),
    updateSegmentSettings: (segmentId, values) =>
        dispatch(actions.updateSegmentSettings(segmentId, values)),
});

AddSegmentFormContainer.propTypes = {
    fetchPreset: PropTypes.func,
    fetchSystemTypes: PropTypes.func,
    editionPayload: PropTypes.object,
    isOpen: PropTypes.bool.isRequired,
    parentFieldSegmentData: PropTypes.object,
    presetsData: PropTypes.array,
    presetScope: PropTypes.string,
    roofTypes: PropTypes.array,
    setAddSegmentModalEditionMode: PropTypes.func,
    setAddSegmentModalOpenStatus: PropTypes.func,
    setDrawingMode: PropTypes.func,
    setNewSegmentConfig: PropTypes.func,
    systemTypes: PropTypes.array,
    updateSegmentSettings: PropTypes.func,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(AddSegmentFormContainer);
